/**
 * Форма жалобы игрока с категорией и текстом (§П5.2).
 */

import { SUPPORT_CATEGORIES, SUPPORT_MESSAGE_MAX_LENGTH, type SupportCategory } from "@hive/shared";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";

import type { SupportReportContext } from "../api/support.js";

export interface ReportProblemSheetProps {
  readonly context?: SupportReportContext;
  readonly busy?: boolean;
  readonly noticeKey?: string | null;
  readonly noticeParams?: Record<string, string>;
  readonly onClose: () => void;
  readonly onSubmit: (category: SupportCategory, message: string) => void;
}

export function ReportProblemSheet({
  context,
  busy = false,
  noticeKey = null,
  noticeParams = {},
  onClose,
  onSubmit,
}: ReportProblemSheetProps): React.JSX.Element {
  const { t } = useTranslation(["support", "common"]);
  const [category, setCategory] = useState<SupportCategory>("money");
  const [message, setMessage] = useState("");

  const contextHint = useMemo((): string | null => {
    if (context?.ledgerEntryId !== undefined) {
      return t("support:report.context.ledger", { id: context.ledgerEntryId });
    }
    if (context?.hexId) {
      return t("support:report.context.hex", { hexId: context.hexId });
    }
    return null;
  }, [context, t]);

  const remaining = SUPPORT_MESSAGE_MAX_LENGTH - message.length;

  return (
    <div className="sheet sheet--report" role="dialog" aria-labelledby="report-title">
      <header className="sheet__head">
        <h2 id="report-title" className="sheet__title">
          {t("support:report.title")}
        </h2>
        <button type="button" className="button sheet__close" onClick={onClose}>
          {t("common:action.close")}
        </button>
      </header>

      {contextHint && <p className="sheet__hint">{contextHint}</p>}

      <label className="sheet__field">
        <span className="sheet__label">{t("support:report.category")}</span>
        <select
          className="sheet__select"
          value={category}
          disabled={busy}
          onChange={(event) => setCategory(event.target.value as SupportCategory)}
        >
          {SUPPORT_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {t(`support:report.category.${value}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="sheet__field">
        <span className="sheet__label">{t("support:report.message")}</span>
        <textarea
          className="sheet__textarea"
          rows={6}
          maxLength={SUPPORT_MESSAGE_MAX_LENGTH}
          placeholder={t("support:report.message.placeholder")}
          value={message}
          disabled={busy}
          onChange={(event) => setMessage(event.target.value)}
        />
        <span className="sheet__counter" aria-live="polite">
          {remaining}
        </span>
      </label>

      {noticeKey && (
        <p className="sheet__notice" role="status">
          {t(noticeKey, noticeParams)}
        </p>
      )}

      <footer className="sheet__actions">
        <button
          type="button"
          className="button button--primary"
          disabled={busy || message.trim().length < 1}
          onClick={() => onSubmit(category, message.trim())}
        >
          {t("support:report.submit")}
        </button>
      </footer>
    </div>
  );
}
